import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";

export const pct = (v) => (v === null || v === undefined ? "–" : `${Math.round(v * 100)}%`);

/**
 * One row per step: how many reached it, and how many of those who reached the
 * step before went on to this one.
 *
 * The bar length is people, on one scale for the whole funnel. The tick on a
 * bar marks how many would have reached the step at the baseline conversion,
 * so a gap between the bar end and the tick is the drop itself. A broken step
 * is red and says "Broken" in words, never by colour alone.
 */
export default function FunnelChart({ steps, baseline = {}, brokenKey = null,
                                      onSelect = null, selectedKey = null }) {
  const [hover, setHover] = useState(null);
  if (!steps?.length) return null;
  const max = Math.max(1, ...steps.map((s) => s.count || 0));

  return (
    <div className="space-y-2.5" data-testid="funnel-chart" role="table" aria-label="Journey funnel">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].count : null;
        const base = baseline[s.key];
        const expected = i > 0 && base != null && prev != null ? prev * base : null;
        const broken = s.key === brokenKey;
        const width = `${((s.count || 0) / max) * 100}%`;
        const tone = broken ? "var(--risk-rev)" : "var(--risk-waiting)";
        return (
          <div key={s.key} role="row" data-testid={`funnel-step-${s.key}`}
               className={`relative rounded-md px-2 py-1.5 ${onSelect ? "cursor-pointer" : ""}`}
               style={{ background: selectedKey === s.key || hover === i ? "var(--overlay-1)" : "transparent" }}
               onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
               onClick={() => onSelect?.(s.key)}>
            <div className="flex items-baseline gap-2 text-[12px] mb-1" role="cell">
              <span className="font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>{i + 1}</span>
              <span className="font-medium truncate">{s.label}</span>
              {broken && (
                <span className="badge b-rev" data-testid="broken-badge">
                  <AlertTriangle size={10} className="inline mr-0.5" />Broken
                </span>
              )}
              <span className="ml-auto font-mono tabular-nums">{(s.count || 0).toLocaleString()}</span>
              <span className="font-mono tabular-nums w-12 text-right" style={{ color: "var(--text-secondary)" }}>
                {i === 0 ? "" : pct(s.conversion)}
              </span>
            </div>
            <div className="relative h-[14px]" role="cell">
              <div className="absolute left-0 top-0 h-full"
                   style={{ width, background: tone, borderRadius: "0 4px 4px 0", minWidth: s.count ? 2 : 0 }} />
              {expected != null && (
                <div className="absolute top-[-3px] h-[20px] w-[2px]" data-testid={`baseline-${s.key}`}
                     title={`Baseline: ${pct(base)} → about ${Math.round(expected).toLocaleString()}`}
                     style={{ left: `calc(${(expected / max) * 100}% - 1px)`, background: "var(--text)" }} />
              )}
            </div>
            {hover === i && i > 0 && (
              <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                {pct(s.conversion)} of {(prev || 0).toLocaleString()} went on from “{steps[i - 1].label}”
                {base != null && <> · baseline {pct(base)}</>}
                {s.median_seconds_from_previous != null &&
                  <> · median {fmtDuration(s.median_seconds_from_previous)}</>}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-3 text-[10.5px] pt-1" style={{ color: "var(--text-muted)" }}>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-[2px] h-[10px]" style={{ background: "var(--text)" }} />
          expected at baseline conversion
        </span>
        <span>% = conversion from the step before</span>
      </div>
    </div>
  );
}

export function fmtDuration(seconds) {
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

/** End-to-end completion over recent evaluations; red dots mark broken windows. */
export function Sparkline({ points, width = 120, height = 28 }) {
  const values = (points || []).filter((p) => p.completion != null);
  if (values.length < 2) {
    return <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>no history yet</span>;
  }
  const max = Math.max(...values.map((p) => p.completion), 0.01);
  const x = (i) => 4 + (i / (values.length - 1)) * (width - 8);
  const y = (v) => height - 4 - (v / max) * (height - 8);
  const d = values.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.completion).toFixed(1)}`).join(" ");
  return (
    <svg width={width} height={height} role="img" data-testid="sparkline"
         aria-label={`Completion ${pct(values[values.length - 1].completion)} in the latest window`}>
      <path d={d} fill="none" stroke="var(--risk-waiting)" strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round" />
      {values.map((p, i) => p.broken && (
        <circle key={i} cx={x(i)} cy={y(p.completion)} r="4" fill="var(--risk-rev)"
                stroke="var(--surface)" strokeWidth="2" />
      ))}
    </svg>
  );
}
